import { Component, HostListener, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import moment from 'moment';
import { DataService } from '../../../shared/services/data.service';

interface SchedulerExecutionReportRow {
  executionDate: string;
  jobName: string;
  exeHistoryId: number;
  platformName: string;
  schedulerPassCount: number;
  schedulerFailCount: number;
  schedulerSkipCount: number;
  schedulerTotalCases: number;
  createdBy: string;
  appVersion: string;
}

@Component({
  selector: 'wtaf-brand-scheduler-report-widget',
  templateUrl: './brand-scheduler-report-widget.component.html',
  styleUrls: ['./brand-scheduler-report-widget.component.css']
})
export class BrandSchedulerReportWidgetComponent implements OnInit {
  brandList: any[] = [];
  platformList: any[] = [];
  reportRows: SchedulerExecutionReportRow[] = [];
  allReportRows: SchedulerExecutionReportRow[] = [];
  userOptions: string[] = [];
  selectedUsers: string[] = [];
  selectedBrandName = '';
  buildName = '';
  platform = '';
  fromDate = '';
  toDate = '';

  showUserDropdown = false;
  loading = false;
  downloadLoading = false;
  errorMessage = '';

  constructor(private dataservice: DataService, private datePipe: DatePipe) {}

  ngOnInit() {
    const today = new Date();
    this.toDate = this.datePipe.transform(today, 'yyyy-MM-dd') || '';
    this.fromDate = moment(today)
      .subtract(7, 'days')
      .format('YYYY-MM-DD');

    this.loadBrands();
    this.loadPlatforms();
  }

  fetchReport() {
    if (!this.isFilterValid()) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.dataservice
      .getSchedulerExecutionReport(
        this.selectedBrandName,
        this.fromDate,
        this.toDate,
        this.buildName,
        this.platform
      )
      .subscribe(
        (response: any) => {
          this.allReportRows = this.extractRows(response);
          this.userOptions = this.extractUserOptions(this.allReportRows);
          this.applyUserFilter();
          this.loading = false;
        },
        () => {
          this.reportRows = [];
          this.allReportRows = [];
          this.userOptions = [];
          this.selectedUsers = [];
          this.showUserDropdown = false;
          this.loading = false;
          this.errorMessage =
            'Unable to fetch scheduler execution report. Please try again.';
        }
      );
  }

  private extractUserOptions(rows: SchedulerExecutionReportRow[]): string[] {
    const users = rows
      .map(row => (row.createdBy || '').trim())
      .filter(user => user !== '');

    return Array.from(new Set(users)).sort((a, b) => a.localeCompare(b));
  }

  toggleUserDropdown() {
    this.showUserDropdown = !this.showUserDropdown;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    if (!target.closest('.user-filter-dropdown')) {
      this.showUserDropdown = false;
    }
  }

  onUserSelectionChange(user: string, checked: boolean) {
    if (!user) {
      return;
    }

    if (checked) {
      if (!this.selectedUsers.includes(user)) {
        this.selectedUsers = [...this.selectedUsers, user];
      }
    } else {
      this.selectedUsers = this.selectedUsers.filter(name => name !== user);
    }

    this.applyUserFilter();
  }

  selectAllUsers() {
    this.selectedUsers = [...this.userOptions];
    this.applyUserFilter();
  }

  clearUserSelection() {
    this.selectedUsers = [];
    this.applyUserFilter();
  }

  isUserSelected(user: string): boolean {
    return this.selectedUsers.includes(user);
  }

  areAllUsersSelected(): boolean {
    return (
      this.userOptions.length > 0 &&
      this.selectedUsers.length === this.userOptions.length
    );
  }

  private applyUserFilter() {
    if (
      !this.selectedUsers.length ||
      this.selectedUsers.length === this.userOptions.length
    ) {
      this.reportRows = [...this.allReportRows];
      return;
    }

    const selected = new Set(this.selectedUsers);
    this.reportRows = this.allReportRows.filter(row =>
      selected.has(row.createdBy)
    );
  }

  get userFilterLabel(): string {
    if (!this.userOptions.length || !this.selectedUsers.length) {
      return 'All Users';
    }

    if (this.selectedUsers.length === this.userOptions.length) {
      return 'All Users';
    }

    if (this.selectedUsers.length === 1) {
      return this.selectedUsers[0];
    }

    return this.selectedUsers.length + ' Users Selected';
  }

  downloadReport() {
    if (!this.isFilterValid()) {
      return;
    }

    this.downloadLoading = true;
    this.errorMessage = '';

    this.dataservice
      .downloadSchedulerExecutionReport(
        this.selectedBrandName,
        this.fromDate,
        this.toDate,
        this.buildName,
        this.platform
      )
      .subscribe(
        response => {
          const fileName = this.getFileNameFromResponse(response);
          const contentType =
            response.headers.get('content-type') ||
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          const body = response.body;

          if (!(body instanceof Blob)) {
            this.downloadLoading = false;
            this.errorMessage =
              'Download failed: server did not return a valid file.';
            return;
          }

          const blob =
            body.type && body.type !== ''
              ? body
              : new Blob([body], { type: contentType });
          const fileURL = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = fileURL;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          setTimeout(() => window.URL.revokeObjectURL(fileURL), 1000);
          this.downloadLoading = false;
        },
        () => {
          this.downloadLoading = false;
          this.errorMessage =
            'Unable to download scheduler execution report. Please try again.';
        }
      );
  }

  private loadBrands() {
    this.dataservice.getAllBrand().subscribe(
      (response: any) => {
        this.brandList = Array.isArray(response) ? response : [];
        if (!this.selectedBrandName && this.brandList.length > 0) {
          this.selectedBrandName = this.brandList[0].brandName;
        }
      },
      () => {
        this.brandList = [];
      }
    );
  }

  private loadPlatforms() {
    this.dataservice.getPlatformByProjectTypeId(2, true).subscribe(
      (response: any) => {
        this.platformList = Array.isArray(response) ? response : [];
      },
      () => {
        this.platformList = [];
      }
    );
  }

  private extractRows(response: any): SchedulerExecutionReportRow[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response.listOfRecords)) {
      return response.listOfRecords;
    }

    return [];
  }

  private isFilterValid(): boolean {
    if (!this.selectedBrandName || !this.fromDate || !this.toDate) {
      this.errorMessage = 'Brand, From date and To date are required.';
      return false;
    }

    this.errorMessage = '';
    return true;
  }

  private getFileNameFromResponse(response: any): string {
    const contentDisposition =
      response.headers.get('content-disposition') || '';
    const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8Match && utf8Match[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const match = /filename=\"?([^\";]+)\"?/i.exec(contentDisposition);

    if (match && match[1]) {
      return match[1].trim();
    }

    return 'scheduler_execution_report.xlsx';
  }
}